import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { AuthService } from "../services/auth.service";
import { CurrentUserService } from "../services/current-user.service";
import { User } from "../../models/user.model";
import { homeRouteFor } from "../auth/home-route";

/**
 * O que falta a pessoa preencher, segundo o backend (spec 018).
 *
 * Aluno e professora passam por onboarding; a **gerente não** — ela é quem
 * conserta o que trava, e retê-la numa tela seria trancar a chave dentro de
 * casa. O corte por papel é do backend: `/users/me` e `/teachers/me` devolvem o
 * mesmo `missingOnboarding`, e a gerente recebe lista vazia. Aqui só se lê.
 *
 * Ausência do campo é tratada como "nada falta": um perfil que não carregou —
 * ou um backend anterior a esta spec — não pode prender ninguém numa tela.
 */
async function missingFields(
  auth: AuthService,
  currentUser: CurrentUserService,
): Promise<string[]> {
  if (!auth.isLoggedIn()) return [];
  // O `CurrentUserService` já escolhe entre /users/me e /teachers/me pelo papel.
  const profile = (await currentUser.load()) as
    | (User & { missingOnboarding?: string[] })
    | null;
  return profile?.missingOnboarding ?? [];
}

/**
 * Retém o aluno na tela de boas-vindas até o cadastro estar completo
 * (spec 018 Task 107).
 *
 * Precisa ser guard, e não um aviso na tela: o painel é uma URL, e um aviso
 * que se pode ignorar digitando o endereço não retém ninguém. O aluno chega
 * aqui vindo de um convite — não tem nome, telefone, CPF nem objetivo, e sem
 * CPF ele nem contrataria um plano.
 */
export const onboardingGuard: CanActivateFn = async () => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const currentUser = inject(CurrentUserService);

  if ((await missingFields(auth, currentUser)).length === 0) {
    return true;
  }

  return router.createUrlTree(["/boas-vindas"]);
};

/**
 * O inverso, para a própria tela de boas-vindas: quem já concluiu e digita a
 * URL volta para o painel, em vez de reencontrar um formulário que já
 * preencheu — e que, submetido de novo, não teria efeito nenhum.
 */
export const onboardingDoneGuard: CanActivateFn = async () => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const currentUser = inject(CurrentUserService);

  if ((await missingFields(auth, currentUser)).length > 0) {
    return true;
  }

  return router.createUrlTree(homeRouteFor(auth));
};

